import { Routes } from '@datadog/browser-rum-react/react-router-v6';
import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import { Route } from 'react-router-dom';

import Admin from '../Admin/Admin';
import { BankLinkPreview } from '../components/BankLinkPreview/BankLinkPreview';
import Branding from '../components/Branding/Branding';
import CreditCheck from '../components/CreditCheck/CreditCheck';
import { ErrorBoundary } from '../components/ErrorBoundary/ErrorBoundary';
import { GenericError } from '../components/GenericError/GenericError';
import { GenericLayout } from '../components/Layout/GenericLayout/GenericLayout';
import { SignupLayout } from '../components/Layout/SignupLayout/SignupLayout';
import MailPrefillUK from '../components/MailPrefill/MailPrefillUK';
import { ShareLinkOpenBanking } from '../components/ShareLinkOpenBanking/ShareLinkOpenBanking';
import { ShareLinkOpenBankingStatus } from '../components/ShareLinkOpenBanking/ShareLinkOpenBankingStatus';
import SignupContainerDE from '../components/SignupContainerDE/SignupContainerDE';
import SignupRoutesUK from '../components/SignupRoutes/SignupRoutesUK/SignupRoutesUK';
import StepRouterUK from '../components/StepRouter/StepRouterUK/StepRouterUK';
import { Connect } from '../Connect/Connect';
import { useEnvProxyForLocale } from '../DeveloperTooling/useEnvProxyForLocale';
import { SignupProvider } from '../hooks/useSignup';
import { messages as deMessages } from '../locales/de/messages';
import { messages as enMessages } from '../locales/en-GB/messages';
import { isIwocaDE } from '../utils/countryHelper';
import { DE_LOCALE, UK_LOCALE } from '../utils/i18n';
import { initialiseTracking } from '../utils/trackingSetup.helpers';

const locale = isIwocaDE() ? DE_LOCALE : UK_LOCALE;

i18n.load({
  [UK_LOCALE]: enMessages,
  [DE_LOCALE]: deMessages,
});
i18n.activate(locale);

export const App = () => {
  initialiseTracking();
  useEnvProxyForLocale();

  return (
    <I18nProvider i18n={i18n}>
      <ErrorBoundary>
        <Routes>
          <Route path="/admin/*" element={<Admin />} />
          <Route path="/connect/*" element={<Connect />} />
          {locale === 'en-GB' && (
            <>
              <Route
                path="/link-open-banking/:token/success"
                element={<ShareLinkOpenBankingStatus status="success" />}
              />
              <Route
                path="/link-open-banking/:token/error"
                element={<ShareLinkOpenBankingStatus status="error" />}
              />

              <Route
                path="/link-open-banking/:token/expired"
                element={<ShareLinkOpenBankingStatus status="expired" />}
              />
              <Route
                path="/link-open-banking/:token"
                element={
                  <GenericLayout>
                    <ShareLinkOpenBanking />
                  </GenericLayout>
                }
              />
              <Route
                path="/link-open-banking/preview-link-bank"
                element={<BankLinkPreview />}
              />
              <Route path="/apply/mail" element={<MailPrefillUK />} />
            </>
          )}
          {/* must precede /:affiliateTag/* so /kreditwunsch/kredit-check isn't captured as affiliateTag=kreditwunsch */}
          {locale === 'de' && (
            <Route
              path="/kreditwunsch/kredit-check/*"
              element={<CreditCheck />}
            />
          )}
          <Route
            path="/:affiliateTag/*"
            element={
              <SignupProvider>
                <Branding>
                  {locale === 'de' ? (
                    <SignupContainerDE />
                  ) : (
                    <SignupLayout>
                      <StepRouterUK>
                        <SignupRoutesUK />
                      </StepRouterUK>
                    </SignupLayout>
                  )}
                </Branding>
              </SignupProvider>
            }
          />
          <Route
            path="/*"
            element={
              <GenericError errorText={<Trans>Page not found.</Trans>} />
            }
          />
        </Routes>
      </ErrorBoundary>
    </I18nProvider>
  );
};
